import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useUser, useUserGiveaways } from '@/hooks/queries'
import { useDocumentMeta } from '@/hooks/use-document-meta'
import { useOverlayStore } from '@/store/overlays'
import { backgroundImage } from '@/lib/format'
import type { Giveaway } from '@/models/giveaway'
import { GiveawayFeed } from '@/components/giveaway/GiveawayFeed'
import { SkeletonLine } from '@/components/ui/Skeleton'

/**
 * A creator's public page at `/u/:username` — who they are and every giveaway
 * they run.
 *
 * The Quasar app rendered owner names with `cursor: pointer` and no handler, so
 * they looked clickable and went nowhere. They link here now, from the feed
 * cards, the giveaway page and the details overlay.
 */
export function UserPage() {
  const { username } = useParams<{ username: string }>()

  const { data: user, isPending: userPending, isError } = useUser(username)
  const { data: giveaways = [], isPending: giveawaysPending } = useUserGiveaways(username)

  const setSelectedGiveaway = useOverlayStore((state) => state.setSelectedGiveaway)
  const openOverlay = useOverlayStore((state) => state.open)

  useDocumentMeta(
    useMemo(
      () =>
        user
          ? {
              title: user.display_name,
              description: `Giveaways run by ${user.display_name} on JustLoot.me.`,
              image: user.display_profile_picture.src,
            }
          : null,
      [user],
    ),
  )

  const select = (giveaway: Giveaway) => {
    setSelectedGiveaway(giveaway)
    openOverlay('giveaway-details')
  }

  if (isError) {
    return (
      <div className="user-page">
        <div className="empty-state">
          <div className="empty-state-title">Creator not found</div>
          <div className="empty-state-body">
            There is no account at <strong>@{username}</strong>.
          </div>
          <Link className="cta" to="/" style={{ marginTop: 12 }}>
            Back to home
          </Link>
        </div>
      </div>
    )
  }

  const live = giveaways.filter((giveaway) => !giveaway.has_ended)
  const ended = giveaways.filter((giveaway) => giveaway.has_ended)

  return (
    <div className="user-page">
      <header className="user-header rise">
        {userPending ? (
          <>
            <div className="skeleton user-avatar" />
            <div className="user-identity" aria-hidden="true">
              <div className="user-name">
                <SkeletonLine width="7em" />
              </div>
              <div className="user-handle">
                <SkeletonLine width="5em" />
              </div>
              <div className="user-stats">
                <span>
                  <strong>–</strong> live
                </span>
                <span>
                  <strong>–</strong> total giveaways
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className="user-avatar"
              style={backgroundImage(user?.display_profile_picture.src)}
              role="img"
              aria-label={`${user?.display_name}'s avatar`}
            />
            <div className="user-identity">
              <h1 className="user-name">{user?.display_name}</h1>
              <div className="user-handle">@{user?.username}</div>
              <div className="user-stats">
                <span>
                  <strong>{live.length}</strong> live
                </span>
                <span>
                  <strong>{giveaways.length}</strong> total giveaways
                </span>
                {user?.display_loot_points ? (
                  <span>
                    <strong>{user.display_loot_points.toLocaleString()}</strong> loot points
                  </span>
                ) : null}
              </div>
            </div>
          </>
        )}
      </header>

      <section className="user-section">
        <div className="section-title">Live giveaways</div>
        <GiveawayFeed
          giveaways={live}
          isPending={giveawaysPending}
          onSelect={select}
          skeletonCount={3}
          emptyTitle="Nothing running"
          emptyBody={`${user?.display_name ?? 'This creator'} has no giveaways open right now.`}
        />
      </section>

      {ended.length > 0 ? (
        <section className="user-section">
          <div className="section-title">Finished</div>
          <GiveawayFeed giveaways={ended} isPending={false} onSelect={select} skeletonCount={0} />
        </section>
      ) : null}
    </div>
  )
}
